import { gql } from '@apollo/client';

/**
 * Marks a career lesson as passed (spec 0018, increment A).
 *
 * Operation name `MarkCareerLessonDone` is deliberately distinct from the
 * backend field `completeCareerLesson` (and its `CompleteCareerLesson` payload
 * type) — equal names collapse the generated result type (graphql-codegen skill).
 *
 * `artifactUrl` is required by the server when the lesson's `completionKind`
 * is 'artifact'; omitting it comes back as a customError on field `artifactUrl`.
 * Repeating the mutation is safe — the backend's unique (member, lesson)
 * progress row is the double-submit guard.
 */
export const MarkCareerLessonDoneMutation = gql`
  mutation MarkCareerLessonDone($lessonId: ID!, $artifactUrl: String) {
    completeCareerLesson(lessonId: $lessonId, artifactUrl: $artifactUrl) {
      success
      reward {
        grantedTitles
        promoCodes
      }
      customErrors {
        field
        message
        code
      }
      lessonsCompleted
      lessonsTotal
    }
  }
`;

/**
 * Marks an unlocked career lesson as OPENED / «просмотрен» (spec 0020 Фаза B).
 *
 * Operation name `TouchCareerLesson` is deliberately distinct from the backend
 * field `markCareerLessonOpened` (and its payload type) — equal names collapse
 * the generated result type (graphql-codegen skill).
 *
 * Fired once when an unlocked lesson mounts. Idempotent: the backend's unique
 * (member, lesson) engagement row is the guard, and opening a lesson NEVER
 * unlocks the next one — it is engagement telemetry, not the completion gate.
 */
export const TouchCareerLessonMutation = gql`
  mutation TouchCareerLesson($lessonId: ID!) {
    markCareerLessonOpened(lessonId: $lessonId) {
      success
      customErrors {
        field
        message
        code
      }
      openCount
    }
  }
`;

/**
 * Autosaves the student's PRIVATE per-lesson notes (spec 0020 Фаза B).
 *
 * Operation name `StoreCareerLessonNotes` is deliberately distinct from the
 * backend field `saveCareerLessonNotes` — equal names collapse the generated
 * result type (graphql-codegen skill).
 *
 * Notes are visible only to the student (server withholds them from ERP), so the
 * panel debounces edits and posts the whole text; the returned `notes` echoes
 * the persisted value.
 */
export const StoreCareerLessonNotesMutation = gql`
  mutation StoreCareerLessonNotes($lessonId: ID!, $notes: String!) {
    saveCareerLessonNotes(lessonId: $lessonId, notes: $notes) {
      success
      customErrors {
        field
        message
        code
      }
      notes
    }
  }
`;

/**
 * Toggles the ☆/★ bookmark on an unlocked career lesson (spec 0020 Фаза C).
 *
 * Operation name `ToggleCareerLessonBookmark` is deliberately distinct from the
 * backend field `setCareerLessonBookmark` — equal names collapse the generated
 * result type (graphql-codegen skill).
 *
 * The desired state is passed explicitly (`isBookmarked`), so a double-tap is
 * idempotent; the returned `isBookmarked` is the source of truth for the button.
 */
export const ToggleCareerLessonBookmarkMutation = gql`
  mutation ToggleCareerLessonBookmark($lessonId: ID!, $isBookmarked: Boolean!) {
    setCareerLessonBookmark(lessonId: $lessonId, isBookmarked: $isBookmarked) {
      success
      customErrors {
        field
        message
        code
      }
      isBookmarked
    }
  }
`;

/**
 * Grades a career-lesson self-check quiz (spec 0020 Фаза C).
 *
 * Operation name `GradeCareerQuiz` is deliberately distinct from the backend
 * field `submitCareerQuiz` (and its payload type) — equal names collapse the
 * generated result type (graphql-codegen skill).
 *
 * `answers` carries one entry per question (`{ questionId, selectedIndexes }`);
 * scoring is exact-set match on the server. The result finally reveals
 * `results.correctIndexes` (hidden until submit) so the UI can show per-question
 * ✓/✗ feedback. Re-submitting is allowed — it simply re-grades («Пройти заново»).
 */
export const GradeCareerQuizMutation = gql`
  mutation GradeCareerQuiz($lessonId: ID!, $answers: [CareerQuizAnswerInput!]!) {
    submitCareerQuiz(lessonId: $lessonId, answers: $answers) {
      success
      customErrors {
        field
        message
        code
      }
      score
      maxScore
      results {
        questionId
        correct
        correctIndexes
      }
    }
  }
`;

/**
 * Records the member's interaction with a vacancy (spec 0018, increment B2).
 *
 * Operation name `SubmitVacancyInteraction` is deliberately distinct from the
 * backend field `markVacancyInteraction` AND from its payload type
 * `MarkVacancyInteraction` — equal names collapse the generated result type
 * (graphql-codegen skill).
 *
 * `kind` is 'saved' or 'applied'. The mutation is idempotent — the backend's
 * unique (member, vacancy, kind) row absorbs a double-tap — and one-way: there
 * is no un-save / un-apply. The returned `vacancy { id myInteraction }` updates
 * the normalized cache entity in place, so the feed row reflects the new state
 * without a refetch (a non-member gets customError code `not_a_member`).
 */
export const SubmitVacancyInteractionMutation = gql`
  mutation SubmitVacancyInteraction($vacancyId: ID!, $kind: String!) {
    markVacancyInteraction(vacancyId: $vacancyId, kind: $kind) {
      success
      customErrors {
        field
        message
        code
      }
      vacancy {
        id
        myInteraction
      }
    }
  }
`;

/**
 * ТЗ 0039 — сдача анкеты урока.
 *
 * Возвращает и отчёт о записи в резюме: карточка итога показывает, что именно
 * попало в CV, а что пропущено (студент уже поправил это руками). Без отчёта
 * обещание «заполнил — наполнилось» пришлось бы принимать на веру.
 *
 * Имя операции отличается от имени поля — иначе codegen схлопывает тип
 * результата в тип схемы.
 */
export const SubmitCareerExerciseMutation = gql`
  mutation SubmitCareerExercise($lessonId: ID!, $answers: [CareerExerciseAnswerInput!]!) {
    submitCareerLessonExercise(lessonId: $lessonId, answers: $answers) {
      success
      reward {
        grantedTitles
        promoCodes
      }
      lessonsCompleted
      lessonsTotal
      submission {
        id
        submittedAt
      }
      applyReport {
        writtenTargets
        skippedTargets
        profileCompleteness
      }
      customErrors {
        field
        message
        code
      }
    }
  }
`;

/** ТЗ 0039 шаг 4 — выбрать трек целью. */
export const ChooseGrowthTrackMutation = gql`
  mutation ChooseGrowthTrack($trackCode: String!) {
    chooseGrowthTrack(trackCode: $trackCode) {
      success
      trackCode
      customErrors {
        code
        field
        message
      }
    }
  }
`;

/** ТЗ 0039 шаг 7 — зафиксировать нажатие и получить бонус. */
export const RegisterShareMutation = gql`
  mutation RegisterShare($channel: String!) {
    registerShare(channel: $channel) {
      success
      result {
        counted
        grantedTitles
        promoCodes
      }
      customErrors {
        code
        field
        message
      }
    }
  }
`;
